var _excluded = ["allowFullscreen", "componentName", "device", "landscapePosterImageProps", "landscapeSource", "orientation", "portraitPosterImageProps", "portraitSource", "presetPrefix"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useMemo, useEffect, useState, forwardRef } from 'react';
import { PropTypes } from 'prop-types';
import Video from './video';
import { validateSource } from './video-util';
var VIDEO_RESOLUTIONS = {
  mobile: '480p',
  tablet: '720p',
  desktop: '1080p'
};
var Scene7VideoSourceProvider = /*#__PURE__*/forwardRef((_ref, ref
// eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  var {
      allowFullscreen,
      componentName,
      device,
      landscapePosterImageProps,
      landscapeSource,
      orientation,
      portraitPosterImageProps,
      portraitSource,
      presetPrefix
    } = _ref,
    playerProps = _objectWithoutProperties(_ref, _excluded);
  var [validatedPortraitSource, setValidatedPortraitSource] = useState(null);
  var [validatedPortraitFullscreenSource, setValidatedPortraitFullscreenSource] = useState(null);
  var [validatedLandscapeSource, setValidatedLandscapeSource] = useState(null);
  var [validatedLandscapeFullscreenSource, setValidatedLandscapeFullscreenSource] = useState(null);
  useEffect(() => {
    /** Validate landscape video sources */
    var isSubscribed = true;
    var landscapeInlineSourceForCurrentDevice = "".concat(landscapeSource).concat(presetPrefix, "_").concat(componentName, "_").concat(device);
    var landscapeFullscreenSourceForCurrentDevice = "".concat(landscapeSource, "_fullscreen_").concat(device, "_").concat(VIDEO_RESOLUTIONS[device]);
    var sourcesValidating = [validateSource(landscapeSource), presetPrefix || componentName ? validateSource(landscapeInlineSourceForCurrentDevice) : false, allowFullscreen ? validateSource(landscapeFullscreenSourceForCurrentDevice) : false];
    Promise.all(sourcesValidating).then(validationStatuses => {
      if (isSubscribed) {
        if (validationStatuses[1]) {
          setValidatedLandscapeSource(landscapeInlineSourceForCurrentDevice);
        } else {
          setValidatedLandscapeSource(validationStatuses[0] ? landscapeSource : null);
        }
        setValidatedLandscapeFullscreenSource(validationStatuses[2] ? landscapeFullscreenSourceForCurrentDevice : null);
      }
    });
    return () => {
      isSubscribed = false;
    };
  }, [device, allowFullscreen, landscapeSource, presetPrefix, componentName]);
  useEffect(() => {
    /** Validate portrait video sources */
    var isSubscribed = true;
    var portraitInlineSourceForCurrentDevice = "".concat(portraitSource).concat(presetPrefix, "_").concat(componentName, "_").concat(device);
    var portraitFullscreenSourceForCurrentDevice = "".concat(portraitSource, "_fullscreen_").concat(device, "_").concat(VIDEO_RESOLUTIONS[device]);
    var sourcesValidating = [portraitSource ? validateSource(portraitSource) : false, portraitSource && presetPrefix && componentName ? validateSource(portraitInlineSourceForCurrentDevice) : false, portraitSource && allowFullscreen ? validateSource(portraitFullscreenSourceForCurrentDevice) : false];
    Promise.all(sourcesValidating).then(validatedStatuses => {
      if (isSubscribed) {
        if (validatedStatuses[1]) {
          setValidatedPortraitSource(portraitInlineSourceForCurrentDevice);
        } else {
          setValidatedPortraitSource(validatedStatuses[0] ? portraitSource : null);
        }
        setValidatedPortraitFullscreenSource(validatedStatuses[2] ? portraitFullscreenSourceForCurrentDevice : null);
      }
    });
    return () => {
      isSubscribed = false;
    };
  }, [device, allowFullscreen, portraitSource, presetPrefix, componentName]);
  var source = useMemo(() => {
    return orientation === 'portrait' && validatedPortraitSource ? validatedPortraitSource : validatedLandscapeSource;
  }, [orientation, validatedPortraitSource, validatedLandscapeSource]);
  var fullscreenSource = useMemo(() => {
    return orientation === 'portrait' && validatedPortraitFullscreenSource ? validatedPortraitFullscreenSource : validatedLandscapeFullscreenSource;
  }, [orientation, validatedPortraitFullscreenSource, validatedLandscapeFullscreenSource]);
  return source && /*#__PURE__*/React.createElement(Video, _extends({
    ref: ref,
    source: source,
    fullscreenSource: fullscreenSource,
    allowFullscreen: allowFullscreen,
    device: device,
    posterImageProps: orientation === 'landscape' ? landscapePosterImageProps || portraitPosterImageProps : portraitPosterImageProps || landscapePosterImageProps
  }, playerProps));
});
Scene7VideoSourceProvider.propTypes = {
  allowFullscreen: PropTypes.bool,
  inline: PropTypes.bool,
  autoplay: PropTypes.bool,
  componentName: PropTypes.string,
  device: PropTypes.string,
  landscapePosterImageProps: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string.isRequired
  }),
  landscapeSource: PropTypes.string.isRequired,
  orientation: PropTypes.string,
  portraitPosterImageProps: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string.isRequired
  }),
  portraitSource: PropTypes.string,
  presetPrefix: PropTypes.string
};
Scene7VideoSourceProvider.defaultProps = {
  allowFullscreen: true,
  inline: true,
  autoplay: false,
  componentName: null,
  device: undefined,
  landscapePosterImageProps: null,
  orientation: undefined,
  portraitPosterImageProps: null,
  portraitSource: null,
  presetPrefix: ''
};
export default Scene7VideoSourceProvider;